import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Portfolio.css';

type ProjectImage = { src: string; label: string };

const projects = [
  {
    id: 'executive-tint',
    tag: 'Commercial',
    filter: 'commercial',
    title: 'Executive Tint',
    location: 'Heights, Houston — 2521 N Durham Dr',
    services: ['Parking Lot Striping', 'ADA Compliance', 'Loading Zone'],
    description: 'Executive Tint\'s parking lot told the wrong story — faded lines, an absent offloading zone, and handicap spaces out of ADA spec. We delivered a full re-stripe with properly dimensioned handicap spaces, a van-accessible stall with the required access aisle, and a clearly marked offloading zone in high-visibility yellow.',
    highlights: [
      'ADA-compliant handicap and van-accessible stalls',
      'Access aisles striped to correct widths',
      'Designated offloading zone with high-visibility yellow',
      'Full lot re-stripe for organized traffic flow',
    ],
    images: [
      { src: '/images/portfolio/executive-tint-before.jpg', label: 'Before — empty lot, no markings' },
      { src: '/images/portfolio/executive-tint-after.jpg',  label: 'After — ADA access aisle and handicap spaces' },
    ] as ProjectImage[],
  },
  {
    id: 'westpark-strip-mall',
    tag: 'Commercial',
    filter: 'commercial',
    title: 'Westpark Outdoor Strip Mall',
    location: 'Westpark Drive, Houston — 9920 Westpark Dr',
    services: ['Pressure Washing', 'Parking Lot Striping', 'ADA Compliance', 'Loading Zones'],
    description: 'One of our most comprehensive commercial jobs to date. The Westpark strip mall required a full two-phase approach: first a complete pressure wash to strip years of oil, grime, and tire marking from the surface, then a full re-stripe covering all stalls, multiple ADA-compliant handicap spaces, and two designated offloading zones.',
    highlights: [
      'Commercial-grade full-lot pressure wash (Phase 1)',
      'Multiple ADA-compliant handicap spaces and access aisles',
      'Two high-visibility offloading zones',
      'All stalls re-striped for maximum capacity',
    ],
    images: [
      { src: '/images/portfolio/westpark-strip-mall-before.jpg',          label: 'Before — faded ADA markings' },
      { src: '/images/portfolio/westpark-strip-mall-after.jpg',           label: 'After — full lot re-stripe with ADA zone' },
      { src: '/images/portfolio/westpark-strip-mall-before-sidewalk.jpg', label: 'Before — dirty sidewalk approach' },
      { src: '/images/portfolio/westpark-strip-mall-after-handicap.jpg',  label: 'After — fresh ADA handicap markings' },
    ] as ProjectImage[],
  },
  {
    id: 'edy-estate',
    tag: 'Residential',
    filter: 'residential',
    title: "Edy's Estate — 5,100+ Sq Ft",
    location: 'Greater Houston Area',
    services: ['Power Washing', 'Pool Area Cleaning'],
    description: 'Our largest residential project to date at over 5,100 square feet of cleanable surface area. Edy\'s Houston estate included a long, stained driveway, a statement roundabout feature, and a full pool area and deck. Years of Houston pollen, heat, algae, and tire marking had dulled what should be a sharp, well-maintained property.',
    highlights: [
      '5,100+ sq ft of surface area cleaned',
      'Full driveway restoration — oil, pollen, and tire marks removed',
      'Roundabout detail work along curb lines and center island',
      'Pool deck degreased and algae-cleared for slip safety',
    ],
    images: [
      { src: '/images/portfolio/edys-estate-before-driveway.jpg', label: 'Before — stained driveway' },
      { src: '/images/portfolio/edys-estate-after-driveway.jpg',  label: 'After — clean driveway' },
      { src: '/images/portfolio/edys-estate-before-pool.jpg',     label: 'Before — pool deck' },
      { src: '/images/portfolio/edys-estate-after-pool.jpg',      label: 'After — restored pool deck' },
    ] as ProjectImage[],
  },
];

type Filter = 'all' | 'commercial' | 'residential';

const Portfolio: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('all');
  const filtered = filter === 'all' ? projects : projects.filter(p => p.filter === filter);

  return (
    <div className="portfolio-page">
      <section className="page-hero section--dark">
        <div className="container-xl">
          <span className="section-label">Our Work</span>
          <h1 className="section-title section-title--white">Project Portfolio</h1>
          <p className="section-sub section-sub--white" style={{ marginTop: '1rem' }}>
            Real jobs. Real results. Every project documented with before-and-after photography and detailed scope records — because our work speaks for itself.
          </p>
        </div>
      </section>
      <div className="red-bar" />

      {/* FILTER */}
      <section className="portfolio-filter-bar">
        <div className="container-xl d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="portfolio-filters d-flex gap-2 flex-wrap">
            {(['all', 'commercial', 'residential'] as Filter[]).map(f => (
              <button
                key={f}
                className={`filter-btn${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All Projects' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <span className="portfolio-count">{filtered.length} project{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section">
        <div className="container-xl">
          <div className="portfolio-list">
            {filtered.map((p, i) => (
              <article className="row g-4 g-lg-5 py-5 border-bottom align-items-start portfolio-project" key={p.id}>
                <div className={`col-12 col-md-6 portfolio-project__images${i % 2 === 1 ? ' order-md-2' : ''}`}>
                  <div className="img-grid">
                    {p.images.length > 0 ? (
                      p.images.slice(0, 4).map((img, j) => (
                        <div className="img-slot" key={j}>
                          <img src={img.src} alt={img.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      ))
                    ) : (
                      Array.from({ length: 4 }).map((_, j) => (
                        <div className="img-slot" key={j}>
                          <div className="img-slot__inner">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="1"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>
                            <span>{j === 0 ? 'Before' : j === 1 ? 'After' : `Photo ${j + 1}`}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="portfolio-project__tag-row d-flex align-items-center gap-3 mt-3">
                    <span className="tag tag--navy">{p.tag}</span>
                    {p.images.length > 4 && <span className="img-more">+{p.images.length - 4} more photos</span>}
                  </div>
                </div>

                <div className={`col-12 col-md-6 portfolio-project__content${i % 2 === 1 ? ' order-md-1' : ''}`}>
                  <h2>{p.title}</h2>
                  <span className="portfolio-project__loc">📍 {p.location}</span>
                  <div className="portfolio-project__services d-flex flex-wrap gap-2 mb-3">
                    {p.services.map((s, j) => (
                      <span className="svc-chip" key={j}>{s}</span>
                    ))}
                  </div>
                  <p className="portfolio-project__desc">{p.description}</p>
                  <ul className="portfolio-project__highlights list-unstyled d-flex flex-column gap-2">
                    {p.highlights.map((h, j) => (
                      <li key={j} className="d-flex align-items-start gap-2">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn btn-brand-primary mt-4">
                    Request Similar Service →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG LINK */}
      <section className="section section--gray">
        <div className="container-xl text-center">
          <span className="section-label">Go Deeper</span>
          <h2 className="section-title section-title--navy mb-3">Read the Full Project Stories</h2>
          <p className="section-sub mx-auto mb-5">
            Each project has a full write-up on our blog — the process, the problem we solved, and what it means for properties like yours.
          </p>
          <Link to="/blog" className="btn btn-brand-navy">Visit Our Blog →</Link>
        </div>
      </section>

      {/* CTA */}
      <section className="section--dark" style={{ padding: 'clamp(3rem,6vw,5rem) 0', borderTop: '3px solid var(--red)' }}>
        <div className="container-xl">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'clamp(1.6rem,3vw,2.5rem)', textTransform: 'uppercase', color: 'var(--white)', marginBottom: '0.5rem' }}>
                Your Property Could Be Next.
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, fontWeight: 300, margin: 0 }}>
                Free assessment. Quote within 24 hours. No obligation.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-3">
              <Link to="/contact" className="btn btn-brand-primary">Get a Free Quote</Link>
              <a href="tel:3464194499" className="btn btn-brand-outline-white">(346) 419-4499</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
